import type { TFunction } from 'i18next'

import React, { useMemo } from 'react'
import {
	createFileRoute,
	Link,
	Outlet,
	useParams,
} from '@tanstack/react-router'
import { InfoIcon, PuzzleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { VideoTutorials } from '@/entities/video-tutorial'

import { PageNavigatorProvider } from '@/shared/components/PageNavigator'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
} from '@/shared/components/ui/sidebar'
import { SettingsTemplateType, VideoTag } from '@/shared/generated'

const getMenuItems = (t: TFunction) => ({
	navMain: [
		{
			title: t(`template.type.${SettingsTemplateType.Scenario}`, {
				defaultValue: 'Scenario',
			}),
			key: SettingsTemplateType.Scenario,
			pathname: `${SettingsTemplateType.Scenario}`,
			items: [
				{
					title: t(`template.type.${SettingsTemplateType.ScenarioStrategy}`, {
						defaultValue: 'Scenario strategy',
					}),
					key: SettingsTemplateType.ScenarioStrategy,
					pathname: `${SettingsTemplateType.ScenarioStrategy}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.ScenarioBusinessConstraints}`,
						{ defaultValue: 'Scenario business rules' }
					),
					key: SettingsTemplateType.ScenarioBusinessConstraints,
					pathname: `${SettingsTemplateType.ScenarioBusinessConstraints}`,
				},
				{
					title: t(`template.type.${SettingsTemplateType.ScenarioStopList}`, {
						defaultValue: 'Scenario stop list',
					}),
					key: SettingsTemplateType.ScenarioStopList,
					pathname: `${SettingsTemplateType.ScenarioStopList}`,
				},
			],
		},
		{
			title: t(`template.type.${SettingsTemplateType.PricingCampaign}`, {
				defaultValue: 'Pricing campaign',
			}),
			key: SettingsTemplateType.PricingCampaign,
			pathname: `${SettingsTemplateType.PricingCampaign}`,
			items: [
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignActivity}`,
						{ defaultValue: 'Activity' }
					),
					key: SettingsTemplateType.PricingCampaignActivity,
					pathname: `${SettingsTemplateType.PricingCampaignActivity}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignProductAssignment}`,
						{ defaultValue: 'Assignments' }
					),
					key: SettingsTemplateType.PricingCampaignProductAssignment,
					pathname: `${SettingsTemplateType.PricingCampaignProductAssignment}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignTacticsRb}`,
						{ defaultValue: 'Tactic rule based' }
					),
					key: SettingsTemplateType.PricingCampaignTacticsRb,
					pathname: `${SettingsTemplateType.PricingCampaignTacticsRb}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignTacticsMd}`,
						{ defaultValue: 'Tactic markdown' }
					),
					key: SettingsTemplateType.PricingCampaignTacticsMd,
					pathname: `${SettingsTemplateType.PricingCampaignTacticsMd}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignTacticsPromo}`,
						{ defaultValue: 'Promo tactics' }
					),
					key: SettingsTemplateType.PricingCampaignTacticsPromo,
					pathname: `${SettingsTemplateType.PricingCampaignTacticsPromo}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignLimits}`,
						{ defaultValue: 'Price limits' }
					),
					key: SettingsTemplateType.PricingCampaignLimits,
					pathname: `${SettingsTemplateType.PricingCampaignLimits}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignRounding}`,
						{ defaultValue: 'Rounding' }
					),
					key: SettingsTemplateType.PricingCampaignRounding,
					pathname: `${SettingsTemplateType.PricingCampaignRounding}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignAnchoring}`,
						{ defaultValue: 'Anchoring' }
					),
					key: SettingsTemplateType.PricingCampaignAnchoring,
					pathname: `${SettingsTemplateType.PricingCampaignAnchoring}`,
				},
				{
					title: t(
						`template.type.${SettingsTemplateType.PricingCampaignMinimalSteps}`,
						{ defaultValue: 'Minimal steps' }
					),
					key: SettingsTemplateType.PricingCampaignMinimalSteps,
					pathname: `${SettingsTemplateType.PricingCampaignMinimalSteps}`,
				},
			],
		},
	],
})

function TemplatesSideBar() {
	const { t } = useTranslation()

	const data = useMemo(() => getMenuItems(t), [])

	return (
		<Sidebar collapsible='none' className='w-72 shrink-0 border-r'>
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{data.navMain.map((item) => (
								<SidebarMenuItem key={item.title}>
									<SidebarMenuButton asChild>
										<Link
											to='/p/templates/$template_id'
											params={{ template_id: item.pathname }}
										>
											{item.title}
										</Link>
									</SidebarMenuButton>
									{item.items?.length ? (
										<SidebarMenuSub>
											{item.items.map((item) => (
												<SidebarMenuSubItem key={item.title}>
													<SidebarMenuSubButton asChild>
														<Link
															to='/p/templates/$template_id'
															params={{ template_id: item.pathname }}
														>
															{item.title}
														</Link>
													</SidebarMenuSubButton>
												</SidebarMenuSubItem>
											))}
										</SidebarMenuSub>
									) : null}
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
		</Sidebar>
	)
}
function TemplatesHost() {
	const { t } = useTranslation()

	const { template_id: type } = useParams({
		from: '/_app/p/templates/$template_id/',
	})

	return (
		<PageNavigatorProvider>
			<SidebarProvider className='min-h-full! items-start'>
				<Layout className='h-full'>
					<Header className='h-12 items-center border-b px-4 py-2'>
						<PuzzleIcon className='size-4' />
						<Separator orientation='vertical' className='mx-2 h-5' />
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbPage>
										{t('templates', { defaultValue: 'Templates' })}
									</BreadcrumbPage>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbPage>
										{t(`template.type.${type}`, { defaultValue: type || '' })}
									</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
						<VideoTutorials tags={[VideoTag.Templates]}>
							<Button size='icon' className='ml-auto' variant='ghost'>
								<InfoIcon />
							</Button>
						</VideoTutorials>
					</Header>
					<Layout row className='h-full overflow-hidden'>
						<TemplatesSideBar />
						<Content className='relative w-full flex-col overflow-y-auto'>
							<Outlet />
						</Content>
					</Layout>
				</Layout>
			</SidebarProvider>
		</PageNavigatorProvider>
	)
}

export const Route = createFileRoute('/_app/p/templates/$template_id')({
	component: TemplatesHost,
})
